// My profile — provider credentials (D6) + Provider Agreement (D5). NPI / medical license / DEA /
// malpractice are PROVIDER credentials, not patient PHI — the sole permitted credential fields in
// this app (D10). The Provider Agreement is a static legal acknowledgment (no backend, nothing
// clinical persisted) ported verbatim from Luan's portal.
//
// The page defaults to a READ-ONLY view (fields shown as disabled values so nothing changes by
// accident). The "Edit" button in the information card header flips every field to editable and
// surfaces Save changes / Cancel; Save persists via the thin profile client and returns to read-only, Cancel
// reverts to the last-saved values. Layout mirrors the patient Account page (horizontal identity
// header card over horizontal field-rows) using the shared settings-card / field-row vocabulary.
import "../styles-profile.css";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { Modal } from "@championcare/ui";
import { useApi } from "../api";
import { useAsync } from "../lib/useAsync";
import { useProfileExtrasApi, type ProfileKpis, type DoctorRichProfile } from "../lib/profileExtrasApi";
import { Loading, ErrorState } from "../components/ui";
import type { ProfileUpdate } from "@forcebase-ai/schema/doctor";

// Working copy of the doctor-set rich fields. languages/focus_areas are real arrays (edited as chips),
// years a digits-only string. Converted to/from the API shape (DoctorRichProfile) on load/save.
interface RichForm {
  bio: string;
  years_experience: string;
  education: string;
  languages: string[];
  focus_areas: string[];
}
const EMPTY_RICH: RichForm = { bio: "", years_experience: "", education: "", languages: [], focus_areas: [] };
function richToForm(r: DoctorRichProfile): RichForm {
  return {
    bio: r.bio ?? "",
    years_experience: r.years_experience == null ? "" : String(r.years_experience),
    education: r.education ?? "",
    languages: r.languages,
    focus_areas: r.focus_areas,
  };
}

// Downscale to a square ≤256px jpeg data: URL — comfortably inside the API's 400KB encoded bound.
async function fileToAvatarDataUrl(file: File): Promise<string> {
  const bitmap = await createImageBitmap(file);
  const side = Math.min(bitmap.width, bitmap.height);
  const size = Math.min(side, 256);
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("canvas unavailable");
  ctx.drawImage(bitmap, (bitmap.width - side) / 2, (bitmap.height - side) / 2, side, side, 0, 0, size, size);
  bitmap.close();
  return canvas.toDataURL("image/jpeg", 0.85);
}

const UploadIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" />
  </svg>
);

const EMPTY: ProfileUpdate = {
  first_name: "",
  last_name: "",
  email: "",
  specialty: "",
  npi: "",
  license_state: "",
  license_number: "",
  license_expires_at: "",
  dea_number: "",
  malpractice_carrier: "",
  malpractice_policy_no: "",
  malpractice_expires_at: "",
};

// One credential/contact field. In view mode it renders the value as a disabled, non-editable
// input (display-only); in edit mode it becomes a live input bound to the form.
function Field({
  label,
  value,
  editing,
  onChange,
  type = "text",
  placeholder,
}: {
  label: string;
  value: string;
  editing: boolean;
  onChange?: (v: string) => void;
  type?: string;
  placeholder?: string;
}): ReactNode {
  return (
    <div className="field">
      <label className="field-label">{label}</label>
      <input
        className="field-input"
        type={type}
        value={value}
        placeholder={placeholder}
        readOnly={!editing}
        disabled={!editing}
        onChange={editing && onChange ? (e) => onChange(e.target.value) : undefined}
      />
    </div>
  );
}

// Multi-line field (doctor bio). Same view/edit behaviour as Field, rendered as a textarea.
function TextareaField({
  label,
  value,
  editing,
  onChange,
  placeholder,
  rows = 4,
}: {
  label: string;
  value: string;
  editing: boolean;
  onChange?: (v: string) => void;
  placeholder?: string;
  rows?: number;
}): ReactNode {
  return (
    <div className="field">
      <label className="field-label">{label}</label>
      <textarea
        className="field-input"
        value={value}
        rows={rows}
        placeholder={placeholder}
        readOnly={!editing}
        disabled={!editing}
        onChange={editing && onChange ? (e) => onChange(e.target.value) : undefined}
      />
    </div>
  );
}

// Number-only field (years of experience). Strips any non-digit input so letters/symbols can't be
// entered, and bounds the value to a sane range.
function NumberField({
  label,
  value,
  editing,
  onChange,
  max = 80,
}: {
  label: string;
  value: string;
  editing: boolean;
  onChange?: (v: string) => void;
  max?: number;
}): ReactNode {
  return (
    <div className="field">
      <label className="field-label">{label}</label>
      <input
        className="field-input"
        type="text"
        inputMode="numeric"
        value={value}
        readOnly={!editing}
        disabled={!editing}
        onChange={
          editing && onChange
            ? (e) => {
                const digits = e.target.value.replace(/[^0-9]/g, "").slice(0, 3);
                const n = digits === "" ? "" : String(Math.min(Number(digits), max));
                onChange(n);
              }
            : undefined
        }
      />
    </div>
  );
}

// Chip/tag input (languages, focus areas). Foolproof: type an item and press Enter (or comma, or the
// Add button) to turn it into a removable pill; ✕ removes one; Backspace on an empty box removes the
// last; a stray draft is committed on blur so nothing is lost. Trims, de-dupes (case-insensitive),
// and caps the count. Stores/returns a clean string[] so downstream rendering never has to parse text.
function ChipsField({
  label,
  values,
  editing,
  onChange,
  placeholder,
  max = 12,
}: {
  label: string;
  values: string[];
  editing: boolean;
  onChange?: (next: string[]) => void;
  placeholder?: string;
  max?: number;
}): ReactNode {
  const [draft, setDraft] = useState("");
  const atCap = values.length >= max;

  function add(raw: string) {
    if (!onChange) return;
    const seen = new Set(values.map((v) => v.toLowerCase()));
    const next = [...values];
    for (const tok of raw.split(",").map((t) => t.trim()).filter(Boolean)) {
      if (next.length >= max) break;
      if (!seen.has(tok.toLowerCase())) {
        next.push(tok);
        seen.add(tok.toLowerCase());
      }
    }
    if (next.length !== values.length) onChange(next);
    setDraft("");
  }
  function removeAt(i: number) {
    onChange?.(values.filter((_, idx) => idx !== i));
  }

  if (!editing) {
    return (
      <div className="field">
        <label className="field-label">{label}</label>
        {values.length ? (
          <div className="chips chips--ro">
            {values.map((v, i) => (
              <span className="chip" key={v + i}>{v}</span>
            ))}
          </div>
        ) : (
          <div className="chips-empty">Not set</div>
        )}
      </div>
    );
  }

  return (
    <div className="field">
      <label className="field-label">{label}</label>
      <div className="chips-edit">
        {values.length > 0 && (
          <div className="chips">
            {values.map((v, i) => (
              <span className="chip" key={v + i}>
                {v}
                <button type="button" className="chip-x" aria-label={`Remove ${v}`} onClick={() => removeAt(i)}>
                  ×
                </button>
              </span>
            ))}
          </div>
        )}
        <div className="chip-input-row">
          <input
            className="field-input chip-input"
            value={draft}
            maxLength={80}
            disabled={atCap}
            placeholder={atCap ? `Up to ${max}` : placeholder}
            onChange={(e) => (e.target.value.includes(",") ? add(e.target.value) : setDraft(e.target.value))}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                add(draft);
              } else if (e.key === "Backspace" && draft === "" && values.length) {
                removeAt(values.length - 1);
              }
            }}
            onBlur={() => draft.trim() && add(draft)}
          />
          <button
            type="button"
            className="pbtn pbtn-ghost pbtn-sm chip-add"
            disabled={!draft.trim() || atCap}
            onMouseDown={(e) => e.preventDefault() /* keep focus; avoid double-commit with onBlur */}
            onClick={() => add(draft)}
          >
            + Add
          </button>
        </div>
      </div>
    </div>
  );
}

const CheckIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const EditIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
  </svg>
);

// Static ~10-section Provider Agreement — content lifted verbatim from Luan's #agreementBackdrop.
function ProviderAgreement({
  open,
  onClose,
  accepted,
  onAccept,
}: {
  open: boolean;
  onClose: () => void;
  accepted: boolean;
  onAccept: () => void;
}) {
  return (
    <Modal open={open} onClose={onClose} wide labelledBy="agreementTitle">
      <div className="prof-agreement">
        <div className="agreement-head">
          <h2 id="agreementTitle">
            Provider <em>Agreement</em>
          </h2>
          <div className="subtitle">Last updated: January 2026 · Version 4.2</div>
        </div>

        <div className="agreement-body">
          <h3>1. Acceptance of Terms</h3>
          <p>
            This Provider Agreement ("Agreement") governs your access to and use of the ChampionCare platform. By
            creating a Provider account, you agree to be bound by this Agreement, the HIPAA Business Associate
            Agreement, and the Platform Privacy Policy, each of which is incorporated by reference. If you do not accept
            these terms, do not create an account.
          </p>

          <h3>2. Eligibility and Credentialing</h3>
          <p>You represent and warrant that you are a licensed healthcare provider in good standing, with:</p>
          <ul>
            <li>A valid, unrestricted license to practice in each state where you provide care;</li>
            <li>A current National Provider Identifier (NPI);</li>
            <li>Active DEA registration where applicable to your prescribing activities;</li>
            <li>No pending disciplinary actions, malpractice judgments, or sanctions.</li>
          </ul>
          <p>
            ChampionCare will verify these credentials at onboarding and on an ongoing basis. You agree to promptly
            notify ChampionCare of any change in your licensure or credentialing status.
          </p>

          <h3>3. Scope of Practice and Standard of Care</h3>
          <p>
            You will provide services only within the scope of your licensure, training, and clinical competency, and
            only to patients located in jurisdictions where you are appropriately licensed. You agree to maintain the
            same standard of care via the platform that applies to in-person practice, including but not limited to
            comprehensive documentation, appropriate diagnostic workup, follow-up planning, and emergency referral
            pathways.
          </p>

          <h3>4. Patient Privacy and HIPAA Compliance</h3>
          <p>
            You will protect all Protected Health Information ("PHI") consistent with the HIPAA Privacy and Security
            Rules and the executed Business Associate Agreement.{" "}
            <strong>Patient communications must occur exclusively through approved platform channels.</strong> You will
            not transmit PHI via personal email, SMS, or unencrypted messaging applications, and you will not share
            patient information outside authorized clinical contexts.
          </p>

          <h3>5. Platform Usage</h3>
          <p>You may use the platform for legitimate clinical purposes only. You agree that you will not:</p>
          <ul>
            <li>Share your account credentials or allow others to access patient records under your identity;</li>
            <li>Access patient charts outside the scope of your direct clinical care;</li>
            <li>Use the platform to violate any applicable law, regulation, or professional standard;</li>
            <li>Reverse engineer, scrape, or attempt to disrupt platform infrastructure.</li>
          </ul>

          <h3>6. Telemedicine and Prescribing Standards</h3>
          <p>
            You agree to comply with all federal and state telemedicine regulations, including state-specific
            requirements for establishing the physician-patient relationship, in-person examination prerequisites, and
            controlled substance prescribing limitations under the Ryan Haight Act. Electronic prescriptions transmitted
            through the platform must comply with DEA EPCS standards.
          </p>

          <h3>7. Compensation and Billing</h3>
          <p>
            Reimbursement rates, payor mix, and payment terms are governed by separate Payor Agreements executed during
            onboarding. You are solely responsible for the accuracy of clinical documentation, CPT/ICD-10 coding, claims
            submission compliance, and applicable tax obligations on amounts received.
          </p>

          <h3>8. Liability and Insurance</h3>
          <p>
            The platform is provided on an "as is" basis.{" "}
            <strong>You remain solely responsible for all clinical decisions and patient outcomes.</strong> ChampionCare
            maintains commercial general liability and cyber liability insurance for platform operations. You must
            maintain individual professional malpractice insurance with minimum limits of{" "}
            <strong>$1,000,000 per occurrence / $3,000,000 aggregate</strong>, naming ChampionCare as an additional
            insured upon request.
          </p>

          <h3>9. Termination</h3>
          <p>
            Either party may terminate this Agreement with thirty (30) days' written notice. ChampionCare may suspend or
            terminate your access immediately upon credential lapse, license suspension, material breach of this
            Agreement, or for the protection of patient safety. Upon termination, you will complete continuity-of-care
            transitions for active patients in accordance with applicable ethical and legal obligations.
          </p>

          <h3>10. Modifications and Governing Law</h3>
          <p>
            ChampionCare may update this Agreement from time to time. Material changes will be communicated with at least
            thirty (30) days' advance notice; continued use of the platform after the effective date constitutes
            acceptance. This Agreement is governed by the laws of the State of Delaware without regard to conflict of law
            principles. Disputes will be resolved through binding arbitration administered by the American Arbitration
            Association.
          </p>

          <p className="agreement-contact">
            Questions about this Agreement? Contact Provider Services at{" "}
            <strong>providers@championcare.com</strong> or (888) 555-CARE.
          </p>
        </div>

        <div className="agreement-foot">
          <div className="note">Scroll to review · Save a copy for your records</div>
          <div className="right">
            <button className="pbtn pbtn-ghost" onClick={onClose}>
              Close
            </button>
            {accepted ? (
              <span className="agreement-accepted">{CheckIcon} Agreement accepted</span>
            ) : (
              <button className="pbtn pbtn-primary" onClick={onAccept}>
                {CheckIcon} I agree
              </button>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
}

// Map a loaded profile payload into the editable form shape.
function toForm(data: NonNullable<ReturnType<typeof useProfileData>["data"]>): ProfileUpdate {
  const p = data.profile;
  const c = data.credentials;
  return {
    first_name: p?.first_name ?? "",
    last_name: p?.last_name ?? "",
    email: p?.email ?? "",
    specialty: p?.specialty ?? "",
    npi: c?.npi ?? "",
    license_state: c?.license_state ?? "",
    license_number: c?.license_number ?? "",
    license_expires_at: c?.license_expires_at?.slice(0, 10) ?? "",
    dea_number: c?.dea_number ?? "",
    malpractice_carrier: c?.malpractice_carrier ?? "",
    malpractice_policy_no: c?.malpractice_policy_no ?? "",
    malpractice_expires_at: c?.malpractice_expires_at?.slice(0, 10) ?? "",
  };
}

// Tiny alias so the toForm return type stays in sync with the loader.
function useProfileData() {
  const api = useApi();
  return useAsync(() => api.profile(), []);
}

export function Profile() {
  const { data, loading, error } = useProfileData();
  // `saved` holds the last-persisted values (the read-only source of truth); `form` is the working
  // copy edited in edit mode. Cancel resets form ← saved.
  const [savedForm, setSavedForm] = useState<ProfileUpdate>(EMPTY);
  const [form, setForm] = useState<ProfileUpdate>(EMPTY);
  // Doctor-set rich fields (patient-facing card). Separate state from the published-schema form.
  const [savedRich, setSavedRich] = useState<RichForm>(EMPTY_RICH);
  const [rich, setRich] = useState<RichForm>(EMPTY_RICH);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [savedToast, setSavedToast] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [agreementOpen, setAgreementOpen] = useState(false);
  const [agreementAccepted, setAgreementAccepted] = useState(false);

  // Profile extras (avatar + KPIs) — additive fields read via a thin client (not in the published SDK).
  const extrasApi = useProfileExtrasApi();
  const [kpis, setKpis] = useState<ProfileKpis | null>(null);
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [photoBusy, setPhotoBusy] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let active = true;
    extrasApi
      .get()
      .then((x) => {
        if (!active) return;
        setKpis(x.kpis);
        setPhotoUrl(x.avatar_url);
        const rf = richToForm(x.rich);
        setRich(rf);
        setSavedRich(rf);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [extrasApi]);

  async function onPickPhoto(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setPhotoBusy(true);
    try {
      const url = await extrasApi.uploadAvatar(await fileToAvatarDataUrl(file));
      setPhotoUrl(url);
    } catch {
      /* keep the current photo on failure */
    } finally {
      setPhotoBusy(false);
    }
  }

  useEffect(() => {
    if (!data) return;
    const next = toForm(data);
    setSavedForm(next);
    setForm(next);
  }, [data]);

  if (loading) return <Loading />;
  if (error) return <ErrorState message={error} />;

  function set<K extends keyof ProfileUpdate>(key: K, value: ProfileUpdate[K]) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  function setRichField<K extends keyof RichForm>(key: K, value: RichForm[K]) {
    setRich((r) => ({ ...r, [key]: value }));
  }

  function startEdit() {
    setForm(savedForm);
    setRich(savedRich);
    setSaveError(null);
    setSavedToast(false);
    setEditing(true);
  }

  function cancelEdit() {
    setForm(savedForm);
    setRich(savedRich);
    setSaveError(null);
    setEditing(false);
  }

  async function save() {
    // Validate the API-required fields up front so a missing credential surfaces as a specific,
    // actionable message instead of the server's generic "Invalid request" 400. NPI + license are
    // mandatory (doctor_credentials is NOT NULL), so a freshly-provisioned provider must complete
    // them before the first save.
    const missing: string[] = [];
    if (!form.first_name.trim()) missing.push("First name");
    if (!form.last_name.trim()) missing.push("Last name");
    if (!form.email.trim()) missing.push("Email");
    if (!form.npi.trim()) missing.push("NPI");
    if (!form.license_state.trim()) missing.push("License state");
    if (!form.license_number.trim()) missing.push("License number");
    if (missing.length) {
      setSaveError(
        `Please complete the required ${missing.length > 1 ? "fields" : "field"}: ${missing.join(", ")}.`,
      );
      return;
    }
    setBusy(true);
    setSaveError(null);
    try {
      // One PUT carries the base credential fields + the doctor-set rich fields (the published
      // saveProfile would strip the rich fields, so we send the whole body via the thin client).
      await extrasApi.saveProfile({
        ...form,
        license_expires_at: form.license_expires_at || undefined,
        malpractice_expires_at: form.malpractice_expires_at || undefined,
        bio: rich.bio.trim() || null,
        years_experience: rich.years_experience === "" ? null : Number(rich.years_experience),
        education: rich.education.trim() || null,
        languages: rich.languages,
        focus_areas: rich.focus_areas,
      });
      setSavedForm(form);
      setSavedRich(rich);
      setEditing(false);
      setSavedToast(true);
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : "We couldn’t save your changes. Please try again.");
    } finally {
      setBusy(false);
    }
  }

  // Read-only view renders from savedForm; edit view renders from the working form.
  const view = editing ? form : savedForm;
  const richView = editing ? rich : savedRich;
  const initials = `${(view.first_name || "D").charAt(0)}${(view.last_name || "R").charAt(0)}`.toUpperCase();

  return (
    <div className="prof">
      <div className="dp-head">
        <div>
          <div className="dp-head__greet">Account &amp; credentials</div>
          <h1 className="dp-head__title">
            My <em>profile.</em>
          </h1>
        </div>
      </div>

      {/* Two-column layout (FB request): identity card left, profile information right. */}
      <div className="prof-grid">
      {/* Identity card (left column) — centered, with KPIs + photo upload. */}
      <div className="settings-card prof-identity">
        <div className="prof-identity__avatar">
          {photoUrl ? <img src={photoUrl} alt="Profile photo" /> : <span>{initials}</span>}
        </div>
        <div className="pt-account__name">
          Dr. {view.first_name} {view.last_name}
        </div>
        {view.specialty && <div className="prof-identity__specialty">{view.specialty}</div>}

        <div className="prof-identity__tags">
          <span className="ptag green">Accepting patients</span>
          <span className="ptag">Telehealth</span>
        </div>

        <div className="prof-identity__rule" />
        <div className="prof-kpis">
          <div className="prof-kpi">
            <span className="prof-kpi__label">Patients</span>
            <span className="prof-kpi__val">{kpis ? kpis.patients.toLocaleString() : "—"}</span>
          </div>
          <div className="prof-kpi">
            <span className="prof-kpi__label">Visits this year</span>
            <span className="prof-kpi__val">{kpis ? kpis.visits_this_year.toLocaleString() : "—"}</span>
          </div>
        </div>

        <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" hidden onChange={onPickPhoto} />
        <button
          className="pbtn pbtn-ghost prof-upload"
          disabled={photoBusy}
          onClick={() => fileRef.current?.click()}
        >
          {UploadIcon} {photoBusy ? "Uploading…" : "Upload photo"}
        </button>
        <button className="prof-agreement-link" onClick={() => setAgreementOpen(true)}>
          View provider agreement
        </button>
      </div>

      {/* Profile information — read-only by default, Edit flips to editable rows */}
      <div className="settings-card">
        <div className="pcard-head">
          <h3>
            Profile <em>information</em>
          </h3>
          {!editing && (
            <button className="pbtn pbtn-ghost pbtn-sm" onClick={startEdit}>
              {EditIcon} Edit
            </button>
          )}
        </div>

        <div className="field-row">
          <Field label="First name" value={view.first_name} editing={editing} onChange={(v) => set("first_name", v)} />
          <Field label="Last name" value={view.last_name} editing={editing} onChange={(v) => set("last_name", v)} />
        </div>
        <div className="field-row">
          <Field label="Email" type="email" value={view.email} editing={editing} onChange={(v) => set("email", v)} />
          <Field
            label="Specialty"
            value={view.specialty ?? ""}
            editing={editing}
            onChange={(v) => set("specialty", v)}
          />
        </div>

        <div className="form-section">Public profile</div>
        <p className="form-section-note">Shown to patients on your profile card (booking, messages, dashboard).</p>
        <div className="field-row field-row--full">
          <TextareaField
            label="Bio"
            value={richView.bio}
            editing={editing}
            onChange={(v) => setRichField("bio", v)}
            placeholder="A short, patient-friendly description of how you practice."
          />
        </div>
        <div className="field-row">
          <NumberField
            label="Years of experience"
            value={richView.years_experience}
            editing={editing}
            onChange={(v) => setRichField("years_experience", v)}
          />
          <Field
            label="Education"
            value={richView.education}
            editing={editing}
            onChange={(v) => setRichField("education", v)}
            placeholder="e.g. Stanford School of Medicine"
          />
        </div>
        <div className="field-row">
          <ChipsField
            label="Languages"
            values={richView.languages}
            editing={editing}
            onChange={(v) => setRichField("languages", v)}
            placeholder="e.g. English"
          />
          <ChipsField
            label="Focus areas"
            values={richView.focus_areas}
            editing={editing}
            onChange={(v) => setRichField("focus_areas", v)}
            placeholder="e.g. Preventive care"
          />
        </div>

        <div className="form-section">Credentials</div>
        <div className="field-row">
          <Field label="NPI number" value={view.npi} editing={editing} onChange={(v) => set("npi", v)} />
          <Field
            label="DEA number (optional)"
            value={view.dea_number ?? ""}
            editing={editing}
            onChange={(v) => set("dea_number", v)}
          />
        </div>
        <div className="field-row">
          <Field
            label="License state"
            value={view.license_state}
            editing={editing}
            onChange={(v) => set("license_state", v)}
          />
          <Field
            label="License number"
            value={view.license_number}
            editing={editing}
            onChange={(v) => set("license_number", v)}
          />
        </div>
        <div className="field-row">
          <Field
            label="License expires"
            type="date"
            value={view.license_expires_at ?? ""}
            editing={editing}
            onChange={(v) => set("license_expires_at", v)}
          />
          <div className="field" />
        </div>
        <div className="field-row">
          <Field
            label="Malpractice carrier"
            value={view.malpractice_carrier ?? ""}
            editing={editing}
            onChange={(v) => set("malpractice_carrier", v)}
          />
          <Field
            label="Malpractice policy #"
            value={view.malpractice_policy_no ?? ""}
            editing={editing}
            onChange={(v) => set("malpractice_policy_no", v)}
          />
        </div>
        <div className="field-row">
          <Field
            label="Malpractice expires"
            type="date"
            value={view.malpractice_expires_at ?? ""}
            editing={editing}
            onChange={(v) => set("malpractice_expires_at", v)}
          />
          <div className="field" />
        </div>

        {(editing || savedToast || saveError) && (
          <div className="form-actions">
            {saveError && (
              <span className="save-error" role="alert">
                {saveError}
              </span>
            )}
            {savedToast && !editing && <span className="saved-note">Saved.</span>}
            {editing && (
              <>
                <button className="pbtn pbtn-ghost" disabled={busy} onClick={cancelEdit}>
                  Cancel
                </button>
                <button className="pbtn pbtn-primary" disabled={busy} onClick={save}>
                  {busy ? "Saving…" : "Save changes"}
                </button>
              </>
            )}
          </div>
        )}
      </div>
      </div>

      <ProviderAgreement
        open={agreementOpen}
        onClose={() => setAgreementOpen(false)}
        accepted={agreementAccepted}
        onAccept={() => setAgreementAccepted(true)}
      />
    </div>
  );
}
